import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment';
import {
  Button, List, message
} from 'antd';
import settings from 'settings';

const { BASE_API_DOMAIN } = settings;
const PATH = `${BASE_API_DOMAIN()}api/report/reports/`;
const { Item } = List;

const ReportWrapper = (WrappedComponent) => {
  const HOC = ({
    report, ...rest
  }) => {
    HOC.form = useRef();
    // get the output types to build buttons
    const { output_types = [] } = report;
    // download state for button
    const [downloading, setDownloading] = useState(false);
    const [id, setId] = useState(null);

    /**
     * @description Format Moment values
     * @return {[String, Number]}
     */
    const formatValue = value => (moment.isMoment(value) ? value.format('YYYY-MM-DD') : value);
    /**
     * @description Build the params
     * @return {String}
     */
    const getParams = params => Object.keys(params)
      .map(key => `${key}=${params[key]}`)
      .join('&');
    /**
     * @description Fetch report from API
     * @param Number {output_type_id}
     * @triggers {loadReport}
     */
    const handleFetch = (output_type_id) => {
      const formInput = HOC.form.current.formInput.getForm(); // use form ref to access unused inputs for API
      const entries = Object.entries(formInput.getFieldsValue()); // get array of object key and value
      const data = entries.reduce((o, key) => ({
        ...o,
        [key[0]]: formatValue(key[1])
      }), {}); // iterate and format moment values
      const { id: reportId } = report;
      // fetch the report content
      axios({
        url: `${PATH}${reportId}/?${getParams({ output_type_id, ...data })}`,
        method: 'GET',
        responseType: 'blob'
      })
        .then((response) => {
          const { headers } = response;
          const disposition = headers['content-disposition']; // get the disposition
          if (disposition && disposition.indexOf('attachment') !== -1) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/; // setup regex
            const matches = filenameRegex.exec(disposition); // find matches for filename
            // if there is a match
            if (matches !== null && matches[1]) {
              const filename = matches[1].replace(/['"]/g, ''); // get the filename
              const url = window.URL.createObjectURL(new Blob([response.data], { type: headers['content-type'] })); // build url
              const link = document.createElement('a');
              // build report
              link.href = url;
              link.setAttribute('download', filename);
              document.body.appendChild(link);
              link.click();
            }
            else {
              throw new Error('Download error: file configured incorrectly');
            }
          }
          else {
            throw new Error('Download error: file configured incorrectly');
          }
        })
        .catch((e) => {
          message.error(`Download error: ${e.message}`);
        })
        .finally(() => {
          setDownloading(false);
        });
    };
    // if downloading or new report id
    useEffect(() => {
      if (downloading) {
        handleFetch(id);
      }
    }, [downloading, id]);
    return (
      <List>
        <Item actions={output_types.map(type => (
          <Button
            loading={downloading}
            disabled={downloading}
            icon="cloud-download"
            type="primary"
            onClick={() => {
              setDownloading(true);
              setId(type.id);
            }}
          >
            {`Download ${type.name}`}
            {' '}
          </Button>
        ))}
        >
          <WrappedComponent
            {...rest}
            ref={HOC.form}
            report={report}
          />
        </Item>
      </List>
    );
  };

  HOC.propTypes = {
    report: PropTypes.object
  };

  HOC.defaultProps = {
    report: {}
  };

  return HOC;
};

export default ReportWrapper;
